import { LabelWithMessage } from '@common/LabelWithMessage';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { DownloadFile } from '@file/DownloadFile';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import {
  Button,
  ComponentType,
  FormTextField,
  Grid,
  IOption,
  Icon,
  LoadingButton,
  Modal,
  Select,
  UseFormReturn,
  removeItemInArray,
} from '@gcl/react-core';
import React, { ReactElement, useMemo } from 'react';
import { useProjectId } from '@config/config.hooks';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import { IProjectTransmittalItemRevision } from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import {
  ETransmittalItemTableFormKey,
  TTransmittalItemTableFormMobileDetailModalType,
} from '../transmittalItemTableForm.constants';
import {
  getTransmittalGeneralDetail,
  useDeleteTransmittalItem,
  useDeleteTransmittalItemRevision,
  useFormPrefix,
  useSiteTransmittalIssueRevision,
  useSubmitRow,
} from '../transmittalItemTableForm.hooks';
import { TransmittalItemTableRowInUseIcon } from '../TransmittalItemTableRowInUse';
import { TransmittalItemFileUpload } from '../TransmittalItemFileUpload';
import { TransmittalItemTableRowTransmittalRevisionForApproval } from '../TransmittalItemTableRowTransmittalRevisionForApproval';

interface ITransmittalItemTableFormMobileDetailModal {
  transmittalId: number;

  item?: IFormFileDrawingItem;
  useFormMethods: UseFormReturn;
  tableIndex: number;
  rowIndex: number;
  setSelectedRow: React.Dispatch<React.SetStateAction<number>>;
  isShowDetailModal: boolean;
  queryKey: QueryKey;
  hasDeletePermission: boolean;
  docType: IProjectTransmittalItemDocumentType;
  setIsBlockRefetch: (value: React.SetStateAction<boolean>) => void;
  setFilesToUpload: React.Dispatch<
    React.SetStateAction<{
      [key: string]: File;
    }>
  >;
  cellExtras: { [key: string]: any };
  setRowUploading: (index: number, isUploading: boolean) => void;
  isReadOnly: boolean;
}
export const TransmittalItemTableFormMobileDetailModal = ({
  transmittalId,
  item,
  useFormMethods,
  tableIndex,
  rowIndex,
  setSelectedRow,
  isShowDetailModal,
  queryKey,
  hasDeletePermission,
  docType,
  setIsBlockRefetch,
  setFilesToUpload,
  setRowUploading,
  cellExtras,
  isReadOnly,
}: ITransmittalItemTableFormMobileDetailModal) => {
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  const { transmittalType, transmittalStatus, masterScheduleId, defaultFileType } =
    getTransmittalGeneralDetail(transmittalId);

  const { tablePrefix, rowPrefix, rowFormPrefix } = useFormPrefix(tableIndex, rowIndex);

  const mode: TTransmittalItemTableFormMobileDetailModalType = useMemo(() => {
    if (
      (transmittalType === ETransmittalType.ICE || transmittalType === ETransmittalType.REVIEWER) &&
      docType.allowUpload
    ) {
      return 'UpdateApprovalRevision';
    }
    if (
      item?.transmittalItem &&
      (transmittalStatus === ETransmittalStatus.APPROVED || !item?.transmittalItem?.inUse || !docType.allowUpload)
    ) {
      return 'View';
    }
    if (!item?.transmittalItem) {
      return 'CreateItem';
    }
    if (item.form?.projectFile?.uploadState === 'finish' && item.transmittalItem.id) {
      return 'UpdateRevision';
    }
    if (
      transmittalType === ETransmittalType.SITE &&
      item.transmittalItem.initiatedProjectTransmittalType === ETransmittalType.DESIGNER
    ) {
      return 'UpdateIssuableRevision';
    }

    return 'UpdateItem';
  }, [JSON.stringify(item), transmittalStatus, transmittalType, JSON.stringify(docType)]);

  const {
    onClickSubmitSingleRow,
    mutationUpsertTransmittalItemsAndRevisions: mutationUpsertTransmittalItemsAndRevisionsSingleRow,
    updateCacheFromUpdateTransmittalItemResponse,
  } = useSubmitRow(masterScheduleId, transmittalId, transmittalType, useFormMethods);

  const {
    isLoading: isLoadingUpsertTransmittalItemsAndRevisionsSingleRow,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisionsSingleRow,
    isError: isErrorUpsertTransmittalItemsAndRevisionsSingleRow,
    error: errorUpsertTransmittalItemsAndRevisionsSingleRow,
    reset: resetUpsertTransmittalItemsAndRevisionsSingleRow,
    data: dataUpsertTransmittalItemsAndRevisionsSingleRow,
  } = mutationUpsertTransmittalItemsAndRevisionsSingleRow;

  const { useDeleteTransmittalItemMutation, openDeleteTransmittalItemModal, renderDeleteTransmittalItemModal } =
    useDeleteTransmittalItem(transmittalId, item, undefined);
  const {
    isLoading: isLoadingDeleteTransmittalItem,
    isError: isErrorDeleteTransmittalItem,
    error: errorDeleteTransmittalItem,
    isSuccess: isSuccessDeleteTransmittalItem,
    reset: resetDeleteTransmittalItem,
  } = useDeleteTransmittalItemMutation;

  const { renderDeleteRevisionModal, openDeleteRevisionModal, useDeleteProjectTransmittalItemRevisionMutation } =
    useDeleteTransmittalItemRevision(
      transmittalId,
      item?.transmittalItem?.latestRevision?.revisionNo,
      item?.transmittalItem?.transmittalRevision?.revisionNo,
      item?.transmittalItem?.transmittalRevision?.id,
      rowIndex,
      undefined,
    );
  const {
    mutate: mutateDeleteProjectTransmittalItemRevision,
    isSuccess: isSuccessDeleteProjectTransmittalItemRevision,
    isLoading: isLoadingDeleteProjectTransmittalItemRevision,
    isError: isErrorDeleteProjectTransmittalItemRevision,
    reset: resetDeleteProjectTransmittalItemRevision,
    error: errorDeleteProjectTransmittalItemRevision,
  } = useDeleteProjectTransmittalItemRevisionMutation;

  const {
    dropdownOptions,
    setLocalValue,
    localValue,
    optionFromItem,
    onMutate: onMutateIssueRevision,
    mutationUpsertTransmittalItemsAndRevisions: mutationUpsertTransmittalItemsAndRevisionsIssueRevision,
    isLatestRevision,
    latestRevisionId,
  } = useSiteTransmittalIssueRevision(masterScheduleId ?? 0, transmittalId, rowIndex, item, docType, undefined);

  const {
    isLoading: isLoadingUpsertTransmittalItemsAndRevisionsIssueRevision,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisionsIssueRevision,
    error: errorUpsertTransmittalItemsAndRevisionsIssueRevision,
    reset: resetUpsertTransmittalItemsAndRevisionsIssueRevision,
  } = mutationUpsertTransmittalItemsAndRevisionsIssueRevision;

  const isAnyActionLoading =
    isLoadingDeleteProjectTransmittalItemRevision ||
    isLoadingDeleteTransmittalItem ||
    isLoadingUpsertTransmittalItemsAndRevisionsIssueRevision ||
    isLoadingUpsertTransmittalItemsAndRevisionsSingleRow;
  return (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessUpsertTransmittalItemsAndRevisionsSingleRow,
            reset: resetUpsertTransmittalItemsAndRevisionsSingleRow,
            message: 'Update Transmittal Item Success',
          },
          {
            isSuccess: isSuccessDeleteTransmittalItem,
            message: 'Delete item success',
            reset: resetDeleteTransmittalItem,
          },
          {
            isSuccess: isSuccessUpsertTransmittalItemsAndRevisionsIssueRevision,
            reset: resetUpsertTransmittalItemsAndRevisionsIssueRevision,
            message: 'Update Transmittal Item Success',
          },
        ]}
        callbackAfterToasted={() => {
          if (isSuccessDeleteTransmittalItem) {
            setSelectedRow(-1);
            queryClient.invalidateQueries(queryKey);

            return;
          }
          useFormMethods.setValue(`${rowFormPrefix}.${ETransmittalItemTableFormKey.lastModifiedDateTime}`, undefined);

          if (isSuccessUpsertTransmittalItemsAndRevisionsSingleRow) {
            updateCacheFromUpdateTransmittalItemResponse(dataUpsertTransmittalItemsAndRevisionsSingleRow?.items);
            setSelectedRow(-1);
            return;
          }
          queryClient.invalidateQueries(queryKey);
        }}
      />
      <ApiErrorDisplay
        errors={[
          {
            error: errorDeleteTransmittalItem,
            genericError: 'Delete item failed',
            reset: resetDeleteTransmittalItem,
          },
        ]}
      />
      <Modal
        title={`${item?.transmittalItem?.docNo ?? 'Create Transmittal Item'}`}
        show={isShowDetailModal}
        onRenderBody={() => {
          if (!item) return <></>;
          return (
            <Grid container spacing={2} className="gcl-gems-transmittal-item-table-form-mobile-form">
              <Grid item xs={12}>
                <LabelWithMessage labelFor={`${rowFormPrefix}.${ETransmittalItemTableFormKey.docNo}`} text="Doc. No." />

                <TransmittalItemTableFormMobileDetailModalGeneralField
                  idleComponent={() => (
                    <span>{useFormMethods.getValues(`${rowFormPrefix}.${ETransmittalItemTableFormKey.docNo}`)}</span>
                  )}
                  fieldComponent={() => (
                    <FormTextField
                      id={`${rowFormPrefix}.${ETransmittalItemTableFormKey.docNo}`}
                      formFieldMethods={useFormMethods}
                    />
                  )}
                  fieldId={ETransmittalItemTableFormKey.docNo}
                  mode={mode}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelWithMessage
                  labelFor={`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`}
                  text="Filename"
                />
                <div>
                  {item?.form?.projectFile?.fileName ??
                    item?.transmittalItem?.transmittalRevision?.projectFile?.fileName ??
                    item?.transmittalItem?.latestRevision?.projectFile?.fileName}
                </div>
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage
                  labelFor={`${rowFormPrefix}.${ETransmittalItemTableFormKey.description}`}
                  text="Description"
                />

                <TransmittalItemTableFormMobileDetailModalGeneralField
                  idleComponent={() => (
                    <span>
                      {useFormMethods.getValues(`${rowFormPrefix}.${ETransmittalItemTableFormKey.description}`)}
                    </span>
                  )}
                  fieldComponent={() => (
                    <FormTextField
                      id={`${rowFormPrefix}.${ETransmittalItemTableFormKey.description}`}
                      formFieldMethods={useFormMethods}
                    />
                  )}
                  fieldId={ETransmittalItemTableFormKey.description}
                  mode={mode}
                />
              </Grid>
              <Grid item xs={6}>
                <LabelWithMessage
                  labelFor={`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`}
                  text="Previously Approved Rev."
                />

                {!isPositiveNumber(item?.transmittalItem?.latestRevision?.projectFile?.id) ? (
                  <Icon iconName="DashCircle" className="gcl-gems-transmittal-item-download-column-not-issued-icon" />
                ) : (
                  <DownloadFile
                    shouldNotChop
                    text={item?.transmittalItem?.latestRevision?.revisionNo}
                    projectId={projectId}
                    attachmentId={item?.transmittalItem?.latestRevision?.projectFile?.id || 0}
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                <LabelWithMessage
                  labelFor={`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`}
                  text="Transmittal Revision"
                />
                <TransmittalItemTableFormMobileDetailModalGeneralField
                  idleComponent={() => {
                    const revision = item?.transmittalItem?.inUse ? (
                      <>
                        {item?.transmittalItem?.inUse &&
                          (!isPositiveNumber(item?.transmittalItem?.transmittalRevision?.projectFile?.id) ? (
                            <div>
                              <Icon
                                iconName="DashCircle"
                                className="gcl-gems-transmittal-item-download-column-not-issued-icon"
                              />
                            </div>
                          ) : (
                            <DownloadFile
                              shouldNotChop
                              text={item?.transmittalItem?.transmittalRevision?.revisionNo}
                              projectId={projectId}
                              attachmentId={item?.transmittalItem?.transmittalRevision?.projectFile?.id || 0}
                            />
                          ))}
                      </>
                    ) : (
                      <div className="gcl-gems-transmittal-item-download-column">
                        <Icon iconName="LockFill" className="gcl-ic-md gcl-fc-danger-400 " />
                      </div>
                    );

                    return (
                      <div
                        className={
                          mode === 'UpdateIssuableRevision'
                            ? 'gcl-gems-transmittal-item-table-form-mobile-form-issuable'
                            : 'gcl-gems-transmittal-item-table-form-mobile-form-transmittal-revision'
                        }
                      >
                        {mode !== 'UpdateApprovalRevision' && revision}
                        {mode === 'UpdateItem' && (
                          <>
                            {hasDeletePermission &&
                              !!item?.transmittalItem?.latestRevision &&
                              item.transmittalItem.transmittalRevision &&
                              docType.allowUpload &&
                              (transmittalType === ETransmittalType.DESIGNER ||
                                transmittalType === ETransmittalType.SITE) && (
                                <Icon
                                  data-testid={`delete-transmittal-item-btn-table-${tableIndex}-row-${rowIndex}`}
                                  iconName="Trash"
                                  className="gcl-ic-btn-lg gcl-fc-danger-400 gcl-mr-xs"
                                  onClick={openDeleteTransmittalItemModal}
                                />
                              )}

                            {
                              <TransmittalItemFileUpload
                                defaultFileType={defaultFileType}
                                setRowState={undefined}
                                setIsBlockRefetch={setIsBlockRefetch}
                                cellExtras={undefined}
                                docType={docType}
                                item={item}
                                rowIndex={rowIndex}
                                tableIndex={tableIndex}
                                useFormMethods={useFormMethods}
                                // newTransmittalItemFile={cellExtras.filesToUpload[item.form.uuid]}
                                newTransmittalItemFileUuid={item.form?.uuid}
                                setFilesToUpload={setFilesToUpload}
                              />
                            }
                          </>
                        )}
                        {mode === 'UpdateApprovalRevision' && (
                          <TransmittalItemTableRowTransmittalRevisionForApproval
                            // rowExtras={rowExtras}
                            cellExtras={cellExtras}
                            projectId={projectId}
                            masterScheduleId={masterScheduleId}
                            tempValue={item.form?.revisionNo ?? ''}
                            item={item}
                            index={rowIndex}
                            transmittalId={transmittalId}
                            transmittalType={transmittalType}
                            transmittalStatus={transmittalStatus}
                            setRowUploading={setRowUploading}
                            docType={docType}
                            onRenderIdle={() => revision}
                            isReadOnly={isReadOnly}
                            queryKey={queryKey}
                          />
                        )}

                        {mode === 'UpdateIssuableRevision' && (
                          <>
                            <Select
                              className="gcl-mr-xxs"
                              id={`${item?.transmittalItem?.id || '0'}_issue_select`}
                              onChange={(newValue: IOption<IProjectTransmittalItemRevision | undefined>[]) => {
                                if (newValue.length > 0) {
                                  setLocalValue(newValue[0]);
                                  onMutateIssueRevision(newValue[0]);
                                }
                              }}
                              renderMenuItem={(revisionObj) => {
                                return (
                                  <>
                                    {revisionObj.text}
                                    {revisionObj.data?.id === latestRevisionId && ' (Latest)'}
                                  </>
                                );
                              }}
                              value={localValue ? [localValue] : undefined}
                              options={dropdownOptions}
                              allowSearch={false}
                              clearable={false}
                              small
                              selectProps={{
                                positionFixed: true,
                              }}
                              disabled={isLoadingUpsertTransmittalItemsAndRevisionsIssueRevision}
                            />
                            {!isLatestRevision && <div className="gcl-gems-transmittal-item-issuable-not-latest " />}
                          </>
                        )}
                      </div>
                    );
                  }}
                  fieldComponent={() => (
                    <FormTextField
                      id={`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`}
                      formFieldMethods={useFormMethods}
                    />
                  )}
                  fieldId={ETransmittalItemTableFormKey.revisionNo}
                  mode={mode}
                />
              </Grid>
              {item && item.form?.item?.type === transmittalType && (
                <Grid item xs={6}>
                  <LabelWithMessage
                    labelFor={`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`}
                    text="In Use"
                  />
                  <TransmittalItemTableRowInUseIcon
                    item={item}
                    transmittalId={transmittalId}
                    transmittalType={transmittalType}
                    transmittalStatus={transmittalStatus}
                    rowIndex={rowIndex}
                    rowState="Idle"
                    onClickToggleInUse={undefined}
                  />
                  {!item.form?.item?.inUse && (
                    <span>
                      {`(This item is removed from use by ${item.transmittalItem?.lastTransmittal?.transmittalNo} ${
                        item.transmittalItem?.lastTransmittalId === transmittalId ? '(this transmittal)' : ''
                      })`}
                    </span>
                  )}
                </Grid>
              )}
            </Grid>
          );
        }}
        onRenderFooter={() => {
          const btns: ReactElement[] = [];

          if (
            hasDeletePermission &&
            !item?.transmittalItem?.latestRevision &&
            item?.transmittalItem?.initiatedProjectTransmittalType === transmittalType
          )
            btns.push(
              <LoadingButton
                disabled={isAnyActionLoading}
                isLoading={isLoadingDeleteTransmittalItem}
                isSuccess={isSuccessDeleteTransmittalItem}
                isError={isErrorDeleteTransmittalItem}
                type={ComponentType.Danger}
                onClick={(e) => {
                  openDeleteTransmittalItemModal(e);
                }}
              >
                Delete Item
              </LoadingButton>,
            );
          if (mode === 'CreateItem' || mode === 'UpdateItem' || mode === 'UpdateRevision')
            btns.push(
              <LoadingButton
                disabled={isAnyActionLoading}
                type={ComponentType.Primary}
                isError={isErrorUpsertTransmittalItemsAndRevisionsSingleRow}
                isSuccess={isSuccessUpsertTransmittalItemsAndRevisionsSingleRow}
                isLoading={isLoadingUpsertTransmittalItemsAndRevisionsSingleRow}
                onClick={() => {
                  onClickSubmitSingleRow(tableIndex, rowIndex, undefined);
                }}
              >
                Save
              </LoadingButton>,
            );
          const closeBtn = (
            <Button
              disabled={isAnyActionLoading}
              type={ComponentType.Secondary}
              onClick={() => {
                setSelectedRow(-1);
              }}
            >
              Close
            </Button>
          );
          const cancelBtn = (
            <Button
              type={ComponentType.Secondary}
              disabled={isAnyActionLoading}
              onClick={() => {
                setSelectedRow(-1);

                useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.docNo}`, {
                  keepDirty: false,
                });
                useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.description}`, {
                  keepDirty: false,
                });
                useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`, {
                  keepDirty: false,
                });

                useFormMethods.resetField(rowPrefix, { keepDirty: false, keepError: false, keepTouched: false });

                if (!item?.transmittalItem) {
                  // cancel new transmittal item
                  const oldFormValues: IFormFileDrawingItem[] = useFormMethods.getValues(tablePrefix) ?? [];
                  useFormMethods.setValue(tablePrefix, removeItemInArray(oldFormValues, rowIndex));
                }
              }}
            >
              {mode === 'CreateItem' ? 'Cancel & Remove File' : 'Cancel'}
            </Button>
          );
          if (mode === 'UpdateIssuableRevision' || mode === 'View' || mode === 'UpdateApprovalRevision')
            btns.push(closeBtn);
          if (mode === 'CreateItem' || mode === 'UpdateItem' || mode === 'UpdateRevision') btns.push(cancelBtn);
          return <>{btns}</>;
        }}
        onHide={() => {
          if (!isAnyActionLoading) setSelectedRow(-1);
        }}
      />
      {renderDeleteTransmittalItemModal(
        isLoadingDeleteTransmittalItem,
        isErrorDeleteTransmittalItem,
        isSuccessDeleteTransmittalItem,
      )}
      {renderDeleteRevisionModal(
        isLoadingDeleteProjectTransmittalItemRevision,
        isErrorDeleteProjectTransmittalItemRevision,
        isSuccessDeleteProjectTransmittalItemRevision,
      )}
    </>
  );
};

interface ITransmittalItemTableFormMobileDetailModalGeneralField {
  idleComponent: () => ReactElement;
  fieldComponent: () => ReactElement;
  fieldId: string;
  mode: TTransmittalItemTableFormMobileDetailModalType;
}
const TransmittalItemTableFormMobileDetailModalGeneralField: (
  props: ITransmittalItemTableFormMobileDetailModalGeneralField,
) => ReactElement = ({
  idleComponent,
  fieldComponent,
  fieldId,
  mode,
}: ITransmittalItemTableFormMobileDetailModalGeneralField) => {
  if (mode === 'View') return idleComponent();

  switch (fieldId) {
    case ETransmittalItemTableFormKey.docNo:
      return mode === 'UpdateIssuableRevision' || mode === 'UpdateApprovalRevision'
        ? idleComponent()
        : fieldComponent();
    case ETransmittalItemTableFormKey.description:
      return mode === 'UpdateIssuableRevision' || mode === 'UpdateApprovalRevision'
        ? idleComponent()
        : fieldComponent();
    case ETransmittalItemTableFormKey.revisionNo:
      return mode !== 'UpdateRevision' && mode !== 'CreateItem' ? idleComponent() : fieldComponent();
    default:
      return <></>;
  }
};
